import { inject, makeStateKey, StateKey, TransferState, Service } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';

import { PlatformService } from '@core/platform/platform.service';
import { StorageService } from '@core/storage/storage.service';
import { LoggerService, ScopedLogger } from '@core/logging/logger.service';
import { CacheKey } from '@core/constants/cache-keys.constants';

/** Persisted cache envelope — value + freshness metadata. `expiresAt: null` ⇒ never expires. */
export interface CacheEnvelope<T> {
  value: T;
  /** Epoch ms the value was written. */
  storedAt: number;
  /** Epoch ms the value goes stale, or `null` for no expiry. */
  expiresAt: number | null;
}

/** Per-operation cache options. */
export interface CacheOptions {
  /** Time-to-live in ms. `0` / `null` / omitted ⇒ no expiry (persist until invalidated). */
  ttlMs?: number | null;
  /** Hand SSR-fetched values to the client via TransferState (default: true). */
  useTransferState?: boolean;
  /** Persist to localStorage in the browser (default: true). */
  persistToStorage?: boolean;
  /** Fired when a fresh value was served from cache (no fetch ran). */
  onHit?: (key: CacheKey) => void;
  /** Fired when the cache was empty / stale (a fetch is about to run). */
  onMiss?: (key: CacheKey) => void;
  /** Fired right after a freshly fetched value is written to the cache. */
  onStored?: (key: CacheKey) => void;
}

/**
 * CacheService — the ONE place in /v2 that turns a key into persisted bytes.
 *
 * Read order : in-memory → TransferState (client hydration) → localStorage.
 * Write fan-out: memory + (server) TransferState + (browser) localStorage.
 *
 * Raw `localStorage` is reached ONLY through {@link StorageService} — the
 * SSR-safe primitive guarded by `isPlatformBrowser()` — so no feature/facade
 * ever touches `localStorage` directly. On the server every browser-only path
 * is skipped (PlatformService.isServer), satisfying the SSR requirement.
 *
 * {@link getOrFetch} is the generic helper every cache-backed facade builds on:
 *   cache HIT  → return the cached value, the `fetchFn` is NEVER called
 *   cache MISS → run `fetchFn` once (concurrent callers share it), store, return
 *
 * Strongly typed (no `any`), root-provided.
 */
@Service()
export class CacheService {
  private readonly platform = inject(PlatformService);
  private readonly storage = inject(StorageService);
  private readonly transferState = inject(TransferState);
  private readonly logger: ScopedLogger = inject(LoggerService).forSource('CacheService');

  /** Hot, process- (browser) / request- (server) lived cache. */
  private readonly memory = new Map<CacheKey, CacheEnvelope<unknown>>();
  /** De-dupes concurrent fetches for the same key. */
  private readonly inFlight = new Map<CacheKey, Observable<unknown>>();

  // ── generic helper ───────────────────────────────────────────────────────
  /**
   * Return the cached value for `key`, or run `fetchFn`, store its result, and
   * return it. Concurrent callers for the same key share a single in-flight
   * fetch. This is the helper all cache-backed facades use.
   */
  getOrFetch<T>(
    key: CacheKey,
    fetchFn: () => Observable<T>,
    options: CacheOptions = {},
  ): Observable<T> {
    const cached = this.read<T>(key, options);
    if (cached != null) {
      options.onHit?.(key);
      return of(cached);
    }

    options.onMiss?.(key);

    const existing = this.inFlight.get(key) as Observable<T> | undefined;
    if (existing) return existing;

    const request$ = fetchFn().pipe(
      tap((value) => {
        this.write<T>(key, value, options);
        options.onStored?.(key);
      }),
      finalize(() => this.inFlight.delete(key)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.inFlight.set(key, request$);
    return request$;
  }

  // ── primitives ─────────────────────────────────────────────────────────────
  /**
   * Read a fresh value for `key`, or `null` if absent / stale / not a v2
   * envelope. The shape guard is essential: these cache keys are SHARED with
   * the v1 app, which persists a different shape (e.g. `{ data, expiresAt }`).
   * Any non-v2 payload is ignored (treated as a miss) so a foreign shape can
   * never surface as an `undefined` value.
   */
  read<T>(key: CacheKey, options: CacheOptions = {}): T | null {
    // 1) in-memory
    const mem = this.memory.get(key);
    if (this.isEnvelope<T>(mem) && this.isFresh(mem)) return mem.value;

    // 2) client hydration from the server (TransferState — consumed once)
    if (this.platform.isBrowser && options.useTransferState !== false) {
      const stateKey = this.stateKeyFor(key);
      if (this.transferState.hasKey(stateKey)) {
        const candidate = this.transferState.get(stateKey, null);
        this.transferState.remove(stateKey);
        if (this.isEnvelope<T>(candidate) && this.isFresh(candidate)) {
          this.logger.trace('Hydrated from TransferState', { key });
          this.memory.set(key, candidate);
          if (options.persistToStorage !== false) this.storage.set(key, candidate);
          return candidate.value;
        }
      }
    }

    // 3) localStorage (browser only; StorageService no-ops on the server)
    if (this.platform.isBrowser && options.persistToStorage !== false) {
      const candidate = this.storage.get<unknown>(key);
      if (this.isEnvelope<T>(candidate) && this.isFresh(candidate)) {
        this.memory.set(key, candidate);
        return candidate.value;
      }
    }

    return null;
  }

  /** Write `value` under `key` to memory + (server) TransferState / (browser) localStorage. */
  write<T>(key: CacheKey, value: T, options: CacheOptions = {}): void {
    const envelope: CacheEnvelope<T> = {
      value,
      storedAt: this.now(),
      expiresAt: this.expiryFrom(options.ttlMs),
    };
    this.memory.set(key, envelope);

    // Server: never touch storage — only seed TransferState for the client.
    if (this.platform.isServer) {
      if (options.useTransferState !== false) this.transferState.set(this.stateKeyFor(key), envelope);
      return;
    }

    // Browser: persist through the SSR-safe storage gate.
    if (options.persistToStorage !== false) this.storage.set(key, envelope);
  }

  /** `true` iff a fresh value exists for `key` (may hydrate from TransferState). */
  has(key: CacheKey, options: CacheOptions = {}): boolean {
    return this.read(key, options) !== null;
  }

  /**
   * Non-mutating freshness probe. Unlike {@link has}/{@link read} it does NOT
   * consume TransferState — safe to call as a guard before deciding to fetch.
   */
  isFreshKey(key: CacheKey, options: CacheOptions = {}): boolean {
    const mem = this.memory.get(key);
    if (this.isEnvelope(mem) && this.isFresh(mem)) return true;
    if (this.platform.isBrowser && options.persistToStorage !== false) {
      const candidate = this.storage.get<unknown>(key);
      if (this.isEnvelope(candidate) && this.isFresh(candidate)) return true;
    }
    return false;
  }

  /** Drop every layer for `key` (memory + in-flight + storage + TransferState). */
  remove(key: CacheKey): void {
    this.memory.delete(key);
    this.inFlight.delete(key);
    this.storage.remove(key);
    const stateKey = this.stateKeyFor(key);
    if (this.transferState.hasKey(stateKey)) this.transferState.remove(stateKey);
    this.logger.trace('Cache invalidated', { key });
  }

  // ── internals ──────────────────────────────────────────────────────────────
  /**
   * Structural guard — `true` only for a well-formed v2 {@link CacheEnvelope}.
   * Rejects `null`/`undefined`, primitives, and FOREIGN shapes persisted under
   * the same (v1-shared) keys — e.g. v1's `{ data, expiresAt }`, which lacks
   * `value`/`storedAt` and would otherwise read back as an `undefined` value.
   */
  private isEnvelope<T>(candidate: unknown): candidate is CacheEnvelope<T> {
    if (candidate === null || typeof candidate !== 'object') return false;
    const env = candidate as Partial<CacheEnvelope<unknown>>;
    return (
      'value' in env &&
      typeof env.storedAt === 'number' &&
      (env.expiresAt === null || typeof env.expiresAt === 'number')
    );
  }

  private isFresh<T>(envelope: CacheEnvelope<T>): boolean {
    return envelope.expiresAt === null || this.now() < envelope.expiresAt;
  }

  private expiryFrom(ttlMs?: number | null): number | null {
    return ttlMs && ttlMs > 0 ? this.now() + ttlMs : null;
  }

  private now(): number {
    return Date.now(); // SSR-safe (Node + browser)
  }

  private stateKeyFor(key: CacheKey): StateKey<CacheEnvelope<unknown> | null> {
    return makeStateKey<CacheEnvelope<unknown> | null>(`cache:${key}`);
  }
}
